import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import JsBarcode from 'jsbarcode';
import { useParams } from "react-router-dom";
import ModuleDetail from "../common/ModuleDetail";
import DataApi from "../../api/dataApi";
import { Card, Col, Row } from "react-bootstrap";
import { convertToUserTimezone } from "../../utils/convertTimeZone";
import { useTimeZone } from "../../contexts/TimeZoneContext";
import jsPDF from 'jspdf';
import 'jspdf-autotable';


const BookDetail = ({ permissions }) => {

  console.log("BookDetail permission prop:", permissions);
  const { id } = useParams();
  const [externalData, setExternalData] = useState({ authors: [], classifications: [], publisher: [], shelf: [], branches: [] });

  const [book, setBook] = useState(null);
  const [relatedModules, setRelatedModules] = useState([]);
  const [librarySettings, setLibrarySettings] = useState(null);
  const { timeZone } = useTimeZone();
  
  
  const fetchBookData = async (bookId) => {
    try {
      const bookApi = new DataApi("book");
      const bookResponse = await bookApi.fetchById(bookId);
      const bookData = bookResponse?.data || {};
      console.log("bookData ->", bookData);

      setRelatedModules(bookData.copies);
      if (bookData.classification_name) {
        bookData.classification_label = bookData.classification_name;
      }
      setBook(bookData);
    } catch (error) {
      console.error("Error fetching book:", error);
    }
  };

  const fetchExternalData = async () => {
    console.log("Fetching external data for BookDetail");
    try {
      
      // 🚀 Add Branch API call
      const branchApi = new DataApi("branches"); 
      const branchResponse = await branchApi.fetchAll();
      const branches = branchResponse?.data?.data || branchResponse?.data || [];

      const authorApi = new DataApi("author");
      const authorsResponse = await authorApi.fetchAll();
      const authors = authorsResponse?.data?.data || authorsResponse?.data || [];

      const classificationApi = new DataApi("classification");
      const classificationsResponse = await classificationApi.fetchAll();
      const classifications = classificationsResponse?.data?.data || classificationsResponse?.data || [];

      const publisherApi = new DataApi("publisher");
      const publisherResponse = await publisherApi.fetchAll();
      const publisher = publisherResponse?.data?.data || publisherResponse?.data || [];

      const shelfApi = new DataApi("shelf");
      const shelfResponse = await shelfApi.fetchAll();
      const shelf = shelfResponse?.data?.data || shelfResponse?.data || [];

      setExternalData({
        authors: Array.isArray(authors) ? authors : [],
        classifications: Array.isArray(classifications) ? classifications : [],
        publisher: Array.isArray(publisher) ? publisher : [],
        shelf: Array.isArray(shelf) ? shelf : [],
        branches: Array.isArray(branches) ? branches : []
      });
    } catch (error) {
      console.error("Error fetching external data:", error);
    }
  };

  const loadLibrarySettings = async () => {
    try {
      const api = new DataApi("librarysettings");
      const resp = await api.get("/all");
      let settings = {};
      if (resp.data && resp.data.success) {
        settings = resp.data.data || {};
      } else if (resp.data) {
        settings = resp.data;
      }
      if (Array.isArray(settings)) settings = settings[0] || {};
      setLibrarySettings(settings);
    } catch (e) {
      console.error("Error loading library settings:", e);
    }
  };

  useEffect(() => {
    fetchExternalData();
    loadLibrarySettings();
    if (id) {
      fetchBookData(id);
    }
  }, [id]);

  useEffect(() => {
    if (book?.classification_id && externalData.classifications.length) {
      const selected = externalData.classifications.find(c => c.id === book.classification_id);
      if (selected) {
        if (!book.classification_category || !book.classification_type) {
          setBook(prev => ({
            ...prev,
            classification_category: selected.category || '',
            classification_type: selected.classification_type || '',
            classification_from: selected.classification_from || '',
            classification_to: selected.classification_to || ''
          }));
        }
      }
    }
  }, [book?.classification_id, externalData.classifications]);

  const inventoryBindings = useMemo(() => [
    { value: "hardcover", label: "Hardcover" },
    { value: "paperback", label: "Paperback" },
    { value: "spiral", label: "Spiral" },
  ], []);

  const fields = useMemo(() => ({
    title: "title",
    subtitle: "isbn",
    details: [
      { key: "title", label: "Title", type: "text", required: true },
      { key: "isbn", label: "ISBN/ISSN", type: "text", required: false, props: { maxLength: 13, pattern: "[0-9]*", inputMode: "numeric", title: "ISBN must be maximum 13 digits (numbers only)" }, validation: { maxLength: 13, pattern: /^[0-9]{0,13}$/ }, errorMessage: "ISBN must be maximum 13 digits (numbers only)" },
      {
        key: "author_id",
        label: "Author",
        type: "select",
        isSearchable: true,
        placeholder: "Search by name...",
        required: true,
        // 🌟 FIX: Mapped array tells Edit Mode exactly what label belongs to what ID
        options: externalData.authors.map(a => ({ value: a.id, label: a.author_name || a.name || a.id })),
        displayKey: "author_name",
        render: (value) => {
          if (!value) return "—";
          const author = externalData.authors.find(a => String(a.id) === String(value));
          return author ? (author.author_name || author.name || value) : value;
        }
      },
      {
        key: "publisher_id",
        label: "Publisher",
        type: "select",
        isSearchable: true,
        placeholder: "Search by name...",
        // 🌟 FIX: Mapped array tells Edit Mode exactly what label belongs to what ID
        options: externalData.publisher.map(p => ({ value: p.id, label: p.publisher_name || p.name || p.id })),
        displayKey: "publisher_name",
        render: (value) => {
          if (!value) return "—";
          const pub = externalData.publisher.find(p => String(p.id) === String(value));
          return pub ? (pub.publisher_name || pub.name || value) : value;
        }
      },
      { key: "classification_code", label: "Class Code", type: "text" },
      {
        key: "classification_id",
        label: "Classification",
        type: "select",
        isSearchable: true,
        displayKey: "classification_name",
        
        // 🌟 FIX: Providing options array here allows the AsyncSelect to instantly resolve the label in Edit Mode!
        options: externalData.classifications.map(c => ({
          value: c.id,
          label: `${c.category || ''} - ${c.name} (${c.code})`
        })),

        getOptionLabel: (value, formData) => {
          if (!value) return "—";
          const classification = externalData.classifications.find(c => String(c.id) === String(value));
          if (classification) {
            return `${classification.category || ''} - ${classification.name} (${classification.code})`;
          }
          return formData?.classification_label || formData?.classification_name || value;
        },

        render: (value, rowData) => {
          if (!value) return "—";
          const classification = externalData.classifications.find(c => String(c.id) === String(value));
          return classification 
            ? (classification.name || classification.classification_name || value) 
            : (rowData?.classification_name || rowData?.classification_label || value);
        },

        loadOptions: async (inputValue) => {
          try {
            const api = (await import('../../api/dataApi')).default;
            const classificationApi = new api('classification');
            const response = await classificationApi.get('/');
            const classifications = response.data || [];
            const filtered = inputValue
              ? classifications.filter(item => {
                const searchValue = inputValue.toLowerCase().trim();
                if (item.category?.toLowerCase().includes(searchValue) ||
                  item.name?.toLowerCase().includes(searchValue) ||
                  item.code?.toLowerCase().includes(searchValue)) {
                  return true;
                }
                if (item.classification_from && item.classification_to) {
                  const isSearchNumeric = /^\d+$/.test(searchValue);
                  if (isSearchNumeric && /^\d+$/.test(item.classification_from)) {
                    const searchNum = parseInt(searchValue);
                    if (searchNum >= parseInt(item.classification_from) &&
                      searchNum <= parseInt(item.classification_to)) return true;
                  }
                }
                return false;
              })
              : classifications;

            return filtered.map(item => ({
              value: item.id,
              label: `${item.category || ''} - ${item.name} (${item.code})`,
              data: item
            }));
          } catch (e) {
            console.error("Error loading classifications:", e);
            return [];
          }
        },

        // Ensure state updates securely
        onChange: (key, value, formData, setFormData) => {
          if (!setFormData) return;

          if (value) {
            const selected = externalData.classifications.find(c => String(c.id) === String(value));
            
            if (selected) {
                const rawShelves = externalData.shelf || [];
                let matchedShelfName = "";
                let matchedSubShelfId = "";

                const matched = rawShelves.find(s => {
                  if (s.classification_type !== selected.classification_type) return false;

                  if (s.classification_type === 'DDC') {
                    const bFrom = parseFloat(selected.classification_from);
                    const bTo = parseFloat(selected.classification_to);
                    const sFrom = parseFloat(s.classification_from);
                    const sTo = parseFloat(s.classification_to);
                    return (bFrom >= sFrom && bTo <= sTo);
                  } else {
                    const bFrom = (selected.classification_from || "").toUpperCase();
                    const bTo = (selected.classification_to || "").toUpperCase();
                    const sFrom = (s.classification_from || "").toUpperCase();
                    const sTo = (s.classification_to || "").toUpperCase();
                    return (bFrom >= sFrom && bTo <= sTo);
                  }
                });

                if (matched) {
                  matchedShelfName = `${matched.floor} - ${matched.rack}`;
                  matchedSubShelfId = matched.id;
                }

                setFormData(prev => ({
                  ...prev,
                  classification_id: value,
                  classification_category: selected.category || '',
                  classification_type: selected.classification_type || '',
                  classification_code: selected.code || '',
                  classification_from: selected.classification_from || '',
                  classification_to: selected.classification_to || '',
                  classification_label: `${selected.category || ''} - ${selected.name} (${selected.code})`,
                  suggested_rack_mapping_id: matchedSubShelfId || prev.suggested_rack_mapping_id
                }));
            } else {
                setFormData(prev => ({ ...prev, classification_id: value }));
            }
          } else {
            setFormData(prev => ({
              ...prev,
              classification_id: "",
              classification_category: "",
              classification_type: "",
              classification_code: "",
              classification_from: "",
              classification_to: "",
              classification_label: ""
            }));
          }
        }
      },
      { key: "classification_type", label: "Classification Type", type: "text", readOnly: true },
      { key: "classification_from", label: "Range From", type: "text", readOnly: true },
      { key: "classification_to", label: "Range To", type: "text", readOnly: true },

      { key: 'min_age', label: 'Min Age', type: 'number' },
      { key: "inventory_binding", label: "Inventory Binding", type: "select", options: inventoryBindings },
      { key: "language", label: "Language", type: "text" },
      { key: "edition", label: "Edition", type: "text" },
      { key: "publication_year", label: "Publication Year", type: "text" },
      { key: "pages", label: "Total Pages", type: "number" },
      { key: "year_of_edition", label: "Year Of Edition", type: "text" },

      { key: "volume", label: "Volume", type: "text" },  
      { key: "branch_id", label: "Branch", isSearchable:true, type: "select", options: externalData.branches ? externalData.branches.map(b => ({ value: b.id, label: b.branch_name})) : [] },
      { key: "status", label: "Status", type: "toggle"  },
    ],
    other: [
      { key: "createdbyid", label: "Created By", type: "text" },
      {
        key: "createddate",
        label: "Created Date",
        type: "date",
        render: (value) => {
          try {            if (!value) return "—";
            return timeZone ? convertToUserTimezone(value, timeZone) : new Date(value).toLocaleDateString();
          } catch (error) {
            console.error("Error converting timezone:", error);
            return value;
          }
        }
      },
      {
        key: "lastmodifieddate", label: "Last Modified Date", type: "date",
        render: (value) => {
          try {            if (!value) return "—";
            return timeZone ? convertToUserTimezone(value, timeZone) : new Date(value).toLocaleDateString();
          } catch (error) {
            console.error("Error converting timezone:", error);
            return value;
          }
        }
      },
      { key: "lastmodifiedbyid", label: "Last Modified By", type: "text" },
    ],
  }), [inventoryBindings, timeZone, externalData]);

  const lookupNavigation = {
    author_id: {
      path: "author",
      idField: "author_id",
      labelField: "author_name"
    },
    classification_id: {
      path: "classification",
      idField: "classification_id",
      labelField: "classification_name"
    },
    publisher_id: {
      path: "publisher",
      idField: "publisher_id",
      labelField: "publisher_name"
    }
  };

  console.log("BookDetail permissions:", permissions);

  const copies = Array.isArray(book?.copies) ? book.copies : [];
  const callNumberLabel = librarySettings?.call_number_label || "Call Number";
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [showBarcodeView, setShowBarcodeView] = useState(false);
  const itemsPerPage = 5;

  const filteredCopies = useMemo(() => {
    if (!searchTerm) return copies;
    const lowerSearch = searchTerm.toLowerCase();
    return copies.filter(copy =>
      copy.barcode?.toLowerCase().includes(lowerSearch) ||
      copy.itemcallnumber?.toLowerCase().includes(lowerSearch) ||
      copy.status?.toLowerCase().includes(lowerSearch) ||
      copy.full_location_code?.toLowerCase().includes(lowerSearch)
    );
  }, [copies, searchTerm]);

  const generateBarcodeCanvas = useCallback((barcodeValue) => {
    const canvas = document.createElement('canvas');
    canvas.width = 300;
    canvas.height = 100;
    const ctx = canvas.getContext('2d');
    try {
      JsBarcode(canvas, barcodeValue, {
        format: "CODE128",
        width: 2.5,
        height: 70,
        margin: 10,
        displayValue: true,
        background: "#ffffff",
        lineColor: "#000000",
        fontSize: 14
      });
      return canvas.toDataURL('image/png');
    } catch (e) {
      console.warn("Barcode generation failed:", e);
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.fillStyle = '#000000';
      ctx.font = 'bold 16px monospace';
      ctx.textAlign = 'center';
      ctx.fillText(barcodeValue, canvas.width / 2, canvas.height / 2 + 6);
      return canvas.toDataURL('image/png');
    }
  }, []);

  const downloadBarcodesPDF = useCallback(() => {
    if (filteredCopies.length === 0) return;

    const doc = new jsPDF("p", "mm", "a4");
    const startX = 10;
    const startY = 20;
    const columns = 5;
    const blockWidth = 38; 
    const blockHeight = 36;

    let index = 0;

    filteredCopies.forEach((copy, i) => {
      const col = index % columns;
      const row = Math.floor(index / columns);

      let x = startX + col * blockWidth;
      let y = startY + row * blockHeight;

      if (y > 260) {
        doc.addPage();
        index = 0;
        const newCol = index % columns;
        const newRow = Math.floor(index / columns);
        x = startX + newCol * blockWidth;
        y = startY + newRow * blockHeight;
      }

      const canvas = document.createElement("canvas");

      JsBarcode(canvas, copy.barcode, {
        format: "CODE128",
        width: 1.8,
        height: 40,
        displayValue: false,
        margin: 10, 
        background: "#ffffff",
        lineColor: "#000000"
      });

      const imgData = canvas.toDataURL("image/png", 1.0);

      let pdfImageHeight = 11;
      const imgRatio = canvas.width / canvas.height;
      let pdfImageWidth = pdfImageHeight * imgRatio; 

      const maxAllowedWidth = blockWidth - 6; 
      
      if (pdfImageWidth > maxAllowedWidth) {
        pdfImageWidth = maxAllowedWidth;
        pdfImageHeight = pdfImageWidth / imgRatio;
      }
            
      const centeredX = x + ((blockWidth - pdfImageWidth) / 2);
      doc.addImage(imgData, "PNG", centeredX, y + 4, pdfImageWidth, pdfImageHeight);
      doc.setFontSize(8);
      doc.setFont("helvetica", "bold");
      doc.text(copy.barcode, x + (blockWidth / 2), y + 4 + pdfImageHeight + 4, { align: "center" });
      index++;
    });

    doc.save(`${book?.title?.replace(/[^a-zA-Z0-9]/g, "_") || "book"}-barcodes.pdf`);
  }, [filteredCopies, book]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentCopies = filteredCopies.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredCopies.length / itemsPerPage);

  const statusBadgeStyle = (status) => {
    const map = {
      AVAILABLE: { background: "#d1fae5", color: "#065f46" },
      BORROWED: { background: "#dbeafe", color: "#1e40af" },
      MAINTENANCE: { background: "#fef3c7", color: "#92400e" },
      LOST: { background: "#fee2e2", color: "#991b1b" },
      DAMAGED: { background: "#f3e8ff", color: "#6b21a8" },
    };
    return map[status] || { background: "#f3f4f6", color: "#374151" };
  };

  return (
    <>
      <Row>
        <Col lg={12} className="mb-3">
          {book && (
            <ModuleDetail
              moduleName="book"
              moduleApi="book"
              moduleLabel="Book"
              icon="fa-solid fa-book"
              fields={fields}
              lookupNavigation={lookupNavigation}
              externalData={externalData}
              data={book}
              fetchBookData={fetchBookData}
              permissions={permissions || {}}
            />
          )}
        </Col>
      </Row>

      {/* Book Copies Table */}
      {book && (
        <Row className="mb-4 px-3">
          <Col lg={12}>
            <Card style={{
              border: "none",
              borderRadius: "12px",
              boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
              overflow: "hidden"
            }}>
              <div style={{
                padding: "16px 24px",
                borderBottom: "1px solid #e9ecef",
                background: "linear-gradient(to right, #f8f9fa, #ffffff)",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between"
              }}>
                <h6 style={{
                  margin: 0,
                  color: "var(--primary-color)",
                  fontSize: "15px",
                  fontWeight: "700",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px"
                }}>
                  <i className="fa-solid fa-layer-group"></i>
                  Inventory / Book Copies
                </h6>
                <div style={{ display: "flex", alignItems: "center", gap: "15px" }}>
                  <div style={{ position: "relative" }}>
                    <i className="fa-solid fa-magnifying-glass" style={{
                      position: "absolute",
                      left: "12px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      color: "#94a3b8",
                      fontSize: "13px"
                    }}></i>
                    <input
                      type="text"
                      placeholder="Search call no..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      style={{
                        padding: "6px 12px 6px 35px",
                        fontSize: "13px",
                        borderRadius: "8px",
                        border: "1px solid #e2e8f0",
                        width: "220px",
                        outline: "none",
                        transition: "all 0.2s"
                      }}
                      onFocus={(e) => e.target.style.borderColor = "var(--primary-color)"}
                      onBlur={(e) => e.target.style.borderColor = "#e2e8f0"}
                    />
                  </div>
                  
                  {filteredCopies.length > 0 && (
                    <button
                      onClick={() => setShowBarcodeView(!showBarcodeView)}
                      style={{
                        background: showBarcodeView ? "#dc3545" : "var(--primary-color)",
                        color: "#fff",
                        border: "none",
                        borderRadius: "6px",
                        padding: "6px 12px",
                        fontSize: "12px",
                        fontWeight: "600",
                        cursor: "pointer",
                        transition: "all 0.2s",
                        whiteSpace: "nowrap"
                      }}
                      title="Toggle Barcode View"
                    >
                      <i className={`fa-solid ${showBarcodeView ? "fa-text-width" : "fa-barcode"} me-1`}></i>
                      {showBarcodeView ? "Text View" : "Barcode View"}
                    </button>
                  )}
                  
                  <span style={{
                    background: "var(--primary-color)",
                    color: "#fff",
                    borderRadius: "20px",
                    padding: "2px 12px",
                    fontSize: "12px",
                    fontWeight: "600"
                  }}>
                    {filteredCopies.length} {filteredCopies.length === 1 ? "Copy" : "Copies"}
                  </span>
                  
                  {filteredCopies.length > 0 && (
                    <button
                      onClick={downloadBarcodesPDF}
                      style={{
                        background: "#28a745",
                        color: "#fff",
                        border: "none",
                        borderRadius: "6px",
                        padding: "6px 12px",
                        fontSize: "12px",
                        fontWeight: "600",
                        cursor: "pointer",
                        transition: "all 0.2s",
                        whiteSpace: "nowrap",
                        ":hover": { background: "#218838" }
                      }}
                      title="Download PDF with all barcodes (scannable)"
                    >
                      <i className="fa-solid fa-download me-1"></i>
                      Download Barcodes
                    </button>
                  )}
                </div>
              </div>

              <Card.Body style={{ padding: 0 }}>
                {filteredCopies.length === 0 ? (
                  <div style={{
                    padding: "40px 16px",
                    textAlign: "center",
                    background: "#f8f9fa"
                  }}>
                    <i className="fa-solid fa-circle-info" style={{ fontSize: "32px", color: "#adb5bd", display: "block", marginBottom: "8px" }}></i>
                    <p style={{ color: "#6c757d", margin: 0, fontSize: "14px" }}>
                      {searchTerm ? `No copies found matching "${searchTerm}"` : "No book copies found for this branch."}
                    </p>
                  </div>
                ) : (
                  <>
                    <div className="table-responsive">
                      <table style={{ width: "100%", fontSize: "14px", borderCollapse: "collapse" }}>
                        <thead>
                          <tr style={{ background: "#f8f9fa", borderBottom: "2px solid #e9ecef" }}>
                            {["#", callNumberLabel, "Status", "Price (₹)", "Rack Location", "Bar Code", "Date Accessioned"].map((label, i) => (
                              <th key={i} style={{
                                padding: "12px 16px",
                                textAlign: "left",
                                fontWeight: "600",
                                color: "#495057",
                                whiteSpace: "nowrap"
                              }}>
                                {label}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {currentCopies.map((copy, idx) => {
                            const badge = statusBadgeStyle(copy.status);
                            const dateStr = copy.date_accessioned
                              ? new Date(copy.date_accessioned).toLocaleDateString("en-IN")
                              : "—";
                            return (
                              <tr
                                key={copy.id || idx}
                                style={{ borderBottom: "1px solid #e9ecef", transition: "background 0.15s" }}
                                onMouseEnter={e => e.currentTarget.style.background = "#f8f9fa"}
                                onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                              >
                                <td style={{ padding: "12px 16px", color: "#6c757d", fontWeight: "600" }}>{indexOfFirstItem + idx + 1}</td>
                                <td style={{ padding: "12px 16px", color: "#374151" }}>
                                  {copy.itemcallnumber || `${copy.cn_class || ""} ${copy.cn_item || ""} ${copy.cn_suffix || ""}`.trim() || "—"}
                                </td>
                                <td style={{ padding: "12px 16px" }}>
                                  <span style={{
                                    ...badge,
                                    borderRadius: "20px",
                                    padding: "3px 12px",
                                    fontSize: "12px",
                                    fontWeight: "600",
                                    display: "inline-block"
                                  }}>
                                    {copy.status || "—"}
                                  </span>
                                </td>
                                <td style={{ padding: "12px 16px", color: "#374151" }}>
                                  {copy.item_price != null ? `₹${parseFloat(copy.item_price).toFixed(2)}` : "—"}
                                </td>
                                <td style={{ padding: "12px 16px", color: "#6c757d", fontFamily: "monospace", fontSize: "12px" }}>
                                  {copy.full_location_code
                                    ? <><i className="fa-solid fa-location-dot me-1" style={{ color: "var(--primary-color)" }}></i>{copy.full_location_code}{copy.rack_name ? ` (${copy.rack_name})` : ""}</>
                                    : <span style={{ color: "#adb5bd" }}>Not assigned</span>
                                  }
                                </td>
                                <td style={{ padding: "12px 8px", verticalAlign: "middle" }}>
                                  {showBarcodeView && copy.barcode ? (
                                    <div
                                      style={{
                                        width: "180px",
                                        height: "90px",
                                        display: "flex",
                                        flexDirection: "column",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        borderRadius: "6px",
                                        padding: "4px"
                                      }}
                                    >
                                      <svg
                                        style={{ width: "100%", height: "60px" }}
                                        ref={(svg) => {
                                          if (svg && copy.barcode) {
                                            try {
                                              svg.innerHTML = "";
                                              JsBarcode(svg, copy.barcode, {
                                                format: "CODE128",
                                                width: 1.4,
                                                height: 55,
                                                margin: 10,
                                                displayValue: false,
                                                background: "#ffffff",
                                                lineColor: "#000000"
                                              });
                                            } catch (e) {
                                              console.warn("Barcode generation failed for", copy.barcode, ":", e);
                                              svg.innerHTML = `<text x="50%" y="50%" text-anchor="middle" fill="#6c757d" font-size="12" font-family="monospace">${copy.barcode}</text>`;
                                            }
                                          }
                                        }}
                                      />

                                      <div
                                        style={{
                                          fontFamily: "monospace",
                                          fontSize: "12px",
                                          letterSpacing: "1px",
                                          marginTop: "2px",
                                          textAlign: "center"
                                        }}
                                      >
                                        {copy.barcode}
                                      </div>
                                    </div>
                                  ) : (
                                    <span
                                      style={{
                                        fontFamily: "monospace",
                                        fontSize: "13px",
                                        letterSpacing: "0.5px"
                                      }}
                                    >
                                      {copy.barcode || "—"}
                                    </span>
                                  )}
                                </td>
                                <td style={{ padding: "12px 16px", color: "#6c757d" }}>{dateStr}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>

                    {totalPages > 1 && (
                      <div className="d-flex justify-content-between align-items-center px-4 py-3" style={{ borderTop: "1px solid #e9ecef", background: "#fdfdfd" }}>
                        <div style={{ color: "#6c757d", fontSize: "13px" }}>
                          Showing <b>{indexOfFirstItem + 1}</b> to <b>{Math.min(indexOfLastItem, filteredCopies.length)}</b> of <b>{filteredCopies.length}</b> copies
                        </div>
                        <ul className="pagination pagination-sm mb-0">
                          <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                            <button className="page-link" onClick={() => setCurrentPage(prev => prev - 1)}>&laquo;</button>
                          </li>
                          {[...Array(totalPages)].map((_, i) => (
                            <li key={i} className={`page-item ${currentPage === i + 1 ? 'active' : ''}`}>
                              <button className="page-link" onClick={() => setCurrentPage(i + 1)}>{i + 1}</button>
                            </li>
                          ))}
                          <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                            <button className="page-link" onClick={() => setCurrentPage(prev => prev + 1)}>&raquo;</button>
                          </li>
                        </ul>
                      </div>
                    )}
                  </>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}
    </>
  );
};

export default BookDetail;